import React, { FC, useContext, useEffect, useState } from "react";
import { useMutation } from "react-query";
import GlobalContext from "src/context/global-context";
import { useIsMounted } from "src/lib/hooks/use-is-mounted";
import client from "../../api";
import {
  APISomethingWrongMsg,
  LIST_PAGE_SIZE,
  manualTransferFilterOptions,
  NetworkDisconnectMsg,
  permissions as permissionConstants,
} from "src/lib/constants";
import usePermissions from "src/hooks/use-permissions";
import PermissionGate from "src/components/shared/permission-gate";
import {
  capitalizeFirstLetter,
  convertPaymentSystem,
  getErrorMessage,
  logger,
  makeNumberWithFloatingDigits,
} from "src/lib/utils";
import {
  CurrencyDataType,
  TransactionDataType,
} from "src/lib/constants/table-type";
import { ColumnsType } from "antd/es/table";
import moment, { utc } from "moment";
import { useNavigate } from "react-router-dom";
import { Table, Tooltip, Modal } from "antd";
import { PaginationInput } from "src/api/types";
import CreateTransactionModal from "src/components/modal-views/createTransactionModal";
import { DateRangePicker } from "rsuite";
import SearchFilter from "src/components/search-filters/transactions-filter";
import { DateRange } from "rsuite/esm/DateRangePicker/types";

interface IManualTransferPage {
  isBuisiness?: boolean;
}
const ManualTransfer: FC<IManualTransferPage> = ({ isBuisiness }) => {
  const targetModule = isBuisiness
    ? permissionConstants.MANUAL_TRANSACTIONS_BUSINESS
    : permissionConstants.MANUAL_TRANSACTIONS_INDIVIDUAL;
  const {
    canView,
    canList,
    canAdd,
    canApproveReject,
  } = usePermissions(targetModule);
  const canViewManualTransfers = canView || canList;

  const { setIsAlertOpened, setAlertText } = useContext(GlobalContext);
  const isMounted = useIsMounted();
  const [dataSource, setDataSource] = useState<TransactionDataType[]>([]);
  const [filter, setFilter] = useState("user_id");
  const [searchText, setSearchText] = useState("");
  const [currencies, setCurrencies] = useState<CurrencyDataType[]>([]);
  const [dateRangeValue, setDateRangeValue] = useState<DateRange | null>(null);
  const navigate = useNavigate();

  const now = new Date();
  const prevMonth = new Date(now.setMonth(now.getMonth() - 1));

  const [startDate, endDate] = dateRangeValue || [];
  const { afterToday } = DateRangePicker;

  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otp, setOtp] = useState<string>("");
  const [otpError, setOtpError] = useState("");

  const [actionType, setActionType] = useState<"approve" | "reject" | null>(
    null
  );
  const [selectedRow, setSelectedRow] = useState<TransactionDataType | null>(
    null
  );
  const [rejectComment, setRejectComment] = useState(""); // new state

  const [total, setTotal] = useState(0);
  const [searchParams, setSearchParams] = useState<PaginationInput>({
    per_page: LIST_PAGE_SIZE,
    current_page: 1,
  });
  const { mutate: getList, isLoading } = useMutation(
    isBuisiness
      ? client.manualTransfers.businessPending
      : client.manualTransfers.individualPending,
    {
      onSuccess: (data) => {
        setTotal(data?.total);

        setDataSource(
          data.data.map((item: any) => ({
            key: item.id,
            user_id: item.user_id,
            asset_id: item.asset_id,
            amount: item.amount,
            amount_fee: item.amount_fee,
            payment_system: item.payment_system,
            status: item.status,
            type: item.type,
            description: item.description,
            comment: item.comment,
            to: item.to,
            withdraw_payment_system: item.withdraw_payment_system,
            refund_id: item.refund_id,
            created_at: item.created_at,
            updated_at: item.updated_at,
            asset_code: item.asset.code,
            // invoice: item.requisites ? true : false,
            // invoice_data: item.requisites,
            full_name: item.full_name,
            created_by_admin_id: item.created_by_admin_id,
            approved_by_admin_id: item.approved_by_admin_id,
            rejected_by_admin_id: item.rejected_by_admin_id,
          }))
        );
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else {
          if (error.response) {
            setAlertText(getErrorMessage(error.response.data));
          } else {
            setAlertText(APISomethingWrongMsg);
          }
        }
        setIsAlertOpened(true);
      },
    }
  );

  const onSelectUser = (userID: number) => {
    if (!canView) return;
    navigate(`/${isBuisiness ? "users-business" : "users"}/${String(userID)}`, {
      replace: true,
    });
  };

  const onPageChange = (page: number, pageSize: number) => {
    setSearchParams({
      ...searchParams,
      current_page: page,
    });
  };

  const onConfirm = (key: number) => {
    if (!canApproveReject) return;
    const selData = dataSource.find((item) => item.key === key);
    if (selData) {
      setActionType("approve");
      setSelectedRow(selData);
      setShowOtpModal(true);
    }
  };

  const onReject = (key: number) => {
    if (!canApproveReject) return;
    const selData = dataSource.find((item) => item.key === key);
    if (selData) {
      setActionType("reject");
      setSelectedRow(selData);
      setShowOtpModal(true);
    }
  };

  const [showCreateModal, setShowCreateModal] = useState(false);

  const handleModalSuccess = () => {
    setAlertText("Transaction Created successfully!");
    setIsAlertOpened(true);
    setShowCreateModal(false);
    getList(searchParams);
  };

  const columns: ColumnsType<TransactionDataType> = [
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          ID
        </p>
      ),
      dataIndex: "key",
      className: "w-auto",
      key: "key",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
            {value}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          User ID
        </p>
      ),
      dataIndex: "user_id",
      className: "w-auto",
      key: "user_id",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          {canView ? (
            <a
              className="font-manrope text-secondary text-[14px] font-medium underline hover:text-primary hover:underline cursor-pointer"
              onClick={() => onSelectUser(value)}
            >
              {value}
            </a>
          ) : (
            <span className="font-manrope text-secondary text-[14px] font-medium">
              {value}
            </span>
          )}
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Created By
        </p>
      ),
      dataIndex: "created_by_admin_id",
      className: "w-auto",
      key: "created_by_admin_id",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {value}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Full name
        </p>
      ),
      dataIndex: "full_name",
      className: "w-auto",
      key: "full_name",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
            {value != "" ? value : "-"}
          </p>
        </div>
      ),
    },

    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Currency
        </p>
      ),
      dataIndex: "asset_code",
      className: "w-auto",
      key: "asset_code",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {value.toUpperCase()}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Amount
        </p>
      ),
      dataIndex: "amount",
      key: "amount",
      className: "w-auto",
      // sorter: (a, b) => a.total_amount - b.total_amount,
      render: (value: number) => (
        <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
          {makeNumberWithFloatingDigits(value, 2)}
        </p>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Type
        </p>
      ),
      dataIndex: "type",
      className: "w-auto",
      key: "type",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
            {capitalizeFirstLetter(value)}
          </p>
        </div>
      ),
    },
    // {
    //   title: () => (
    //     <p className="font-manrope text-secondary text-[14px] font-medium">
    //       Payment Method
    //     </p>
    //   ),
    //   dataIndex: "payment_system",
    //   className: "w-auto",
    //   key: "payment_system",
    //   render: (value) => (
    //     <div className="flex flex-row gap-2 items-center">
    //       <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
    //         {convertPaymentSystem(value)}
    //       </p>
    //     </div>
    //   ),
    // },

    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Status
        </p>
      ),
      dataIndex: "status",
      className: "w-auto",
      key: "status",
      render: (value) => {
        let color = "text-gray-600"; // default
        if (value === "completed") color = "text-green-600";
        else if (value === "canceled") color = "text-red-500";
        else if (value === "waiting") color = "text-yellow-500";

        return (
          <div className="flex flex-row gap-2 items-center">
            <p
              className={`font-manrope text-[13px] sm:text-[14px] font-medium ${color}`}
            >
              {capitalizeFirstLetter(value)}
            </p>
          </div>
        );
      },
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Comment
        </p>
      ),
      dataIndex: "comment",
      className: "w-auto",
      key: "comment",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
            {value}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Date
        </p>
      ),
      dataIndex: "created_at",
      className: "w-auto",
      key: "created_at",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
            {/* {moment.unix(value).format("YYYY-MM-DD HH:mm:ss")} */}
            {moment.utc(value).local().format("YYYY-MM-DD HH:mm:ss")}{" "}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Approved By
        </p>
      ),
      dataIndex: "approved_by_admin_id",
      className: "w-auto",
      key: "approved_by_admin_id",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {value ? (
              value
            ) : (
              <p className="font-manrope text-gray-400 text-[28px] font-bold text-center">
                —
              </p>
            )}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Rejected By
        </p>
      ),
      dataIndex: "rejected_by_admin_id",
      className: "w-auto",
      key: "rejected_by_admin_id",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {value ? (
              value
            ) : (
              <p className="font-manrope text-gray-400 text-[28px] font-bold text-center">
                —
              </p>
            )}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Action
        </p>
      ),
      dataIndex: "action",
      className: "w-[100px]",
      render: (_, record) => {
        if (!canApproveReject) {
          return (
            <p className="font-manrope text-gray-400 text-[28px] font-bold text-center">
              —
            </p>
          );
        }
        if (record.status === "waiting") {
          return (
            <div className="flex flex-row gap-4">
              <a
                className="text-success text-[14px] font-manrope font-medium cursor-pointer"
                onClick={() => onConfirm(record.key as number)}
              >
                Confirm
              </a>
              <a
                className="text-error text-[14px] font-manrope font-medium cursor-pointer"
                onClick={() => onReject(record.key as number)}
              >
                Reject
              </a>
            </div>
          );
        } else {
          return (
            <p className="font-manrope text-gray-400 text-[28px] font-bold text-center">
              —
            </p>
          );
        }
      },
    },
  ];

  useEffect(() => {
    const timer = setTimeout(() => {
      if (isMounted && canViewManualTransfers) {
        logger.log("called", searchParams);
        getList({
          ...searchParams,
          search_value: searchText.length > 0 ? searchText : undefined,
          search_field: searchText.length > 0 ? filter : undefined,
          start_date: startDate
            ? moment(startDate).format("yyyy-MM-DD")
            : undefined,
          end_date: endDate ? moment(endDate).format("yyyy-MM-DD") : undefined,
        });
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [
    isMounted,
    canViewManualTransfers,
    searchParams.current_page,
    searchText,
    startDate,
    endDate,
    isBuisiness,
  ]);

  useEffect(() => {
    // Reset current page to 1 whenever filter or search text changes
    setSearchParams((prev) => ({
      ...prev,
      current_page: 1,
    }));
  }, [searchText]);

  const { mutate: processTransaction, isLoading: isProcessing } = useMutation(
    async () => {
      if (!selectedRow || !actionType) {
        throw new Error("Missing transaction context");
      }
      logger.log("selectedrow and action type ", selectedRow, actionType);

      const isDeposit = selectedRow.type === "deposit";

      if (actionType === "approve") {
        return await client.manualTransfers.confirmTransfer({
          id: Number(selectedRow.key),
          otp,
        });
      } else {
        // reject
        return await client.manualTransfers.cancelTransfer({
          id: Number(selectedRow.key),
          otp,
          comment: rejectComment.trim(), // pass the comment
        });
      }
    },
    {
      onSuccess: () => {
        setShowOtpModal(false);
        setOtp("");
        setOtpError("");
        setSelectedRow(null);
        setActionType(null);

        getList(searchParams);
        setAlertText("Transaction processed successfully!");
        setIsAlertOpened(true);
      },
      onError: (error: any) => {
        setOtpError("");
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
          setIsAlertOpened(true);
        } else if (error.response) {
          setAlertText(getErrorMessage(error.response.data));
          setIsAlertOpened(true);
        } else {
          setAlertText(APISomethingWrongMsg);
          setIsAlertOpened(true);
        }
      },
    }
  );

  const handleOtpSubmit = () => {
    if (!canApproveReject) return;
    if (!otp || otp.length !== 6) {
      setOtpError("Enter a valid 6-digit OTP");
      return;
    }
    if (actionType === "reject" && !rejectComment.trim()) {
      setOtpError("Please provide a comment for rejection");
      return;
    }

    processTransaction();
  };

  if (!canViewManualTransfers) {
    return (
      <div className="w-full h-full bg-gray">
        <div className="w-full flex flex-col">
          <p className="text-[25px] sm:text-[32px] font-manrope text-primary font-medium leading-[37.5px] sm:leading-[48px]">
            Manual Transactions
          </p>
          <div className="bg-white rounded-[12px] p-6 mt-4 text-center text-secondary">
            You do not have permission to view manual transactions.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-gray">
      <div className="w-full flex flex-col">
        <p className="text-[25px] sm:text-[32px] font-manrope text-primary font-medium leading-[37.5px] sm:leading-[48px]">
          Manual Transactions
        </p>
        <div className="mt-4 flex items-end gap-2 justify-between flex-wrap w-full mb-4">
          <div className="flex items-center gap-2 flex-wrap">
            <SearchFilter
              options={manualTransferFilterOptions}
              value={filter}
              onChange={setFilter}
              searchText={searchText}
              setSearchText={setSearchText}
              currencies={currencies}
            />
            <DateRangePicker
              format="dd/MM/yyyy"
              size="lg"
              block
              placement="bottomEnd"
              style={{ width: "308px" }}
              value={dateRangeValue}
              onChange={setDateRangeValue}
              defaultCalendarValue={[prevMonth, now]}
              shouldDisableDate={afterToday()}
              ranges={[]}
            />
          </div>
          <PermissionGate module={[targetModule]} action="can_add">
            <div className="ml-auto">
              <button
                className="px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark"
                onClick={() => {
                  if (!canAdd) return;
                  setShowCreateModal(true);
                }}
              >
                Add Transaction
              </button>
            </div>
          </PermissionGate>
        </div>

        <div className="custom-scroll flex flex-col w-full bg-white rounded-[12px] p-4 mt-2 sm:mt-4">
          <Table
            loading={isLoading}
            showSorterTooltip={false}
            columns={columns}
            dataSource={dataSource}
            pagination={{
              total,
              current: searchParams.current_page,
              onChange: onPageChange,
              position: ["bottomCenter"],
              defaultPageSize: LIST_PAGE_SIZE,
              showSizeChanger: false,
            }}
            scroll={{ x: "max-content" }}
          />
        </div>
        {showCreateModal && canAdd && (
          <CreateTransactionModal
            isBusiness={isBuisiness}
            onClose={() => setShowCreateModal(false)}
            onSuccess={handleModalSuccess}
          />
        )}
        {/* OTP Modal */}
        <Modal
          open={showOtpModal}
          title={`Enter 2FA code to ${
            actionType === "approve" ? "approve" : "reject"
          } transaction`}
          okButtonProps={{
            // disabled: !isVerified,
            style: {
              backgroundColor: "#1890ff", // Ant Design primary blue
              color: "#fff", // Text color white
              border: "none",
            },
          }}
          onOk={handleOtpSubmit}
          onCancel={() => {
            setShowOtpModal(false);
            setOtp("");
          }}
          okText="Confirm"
          confirmLoading={isProcessing} // 👈 shows spinner on confirm
        >
          {actionType === "reject" && (
            <div className="flex flex-col">
              <label>Rejection Comment</label>
              <textarea
                className="w-full border px-2 py-1 rounded"
                value={rejectComment}
                onChange={(e) => setRejectComment(e.target.value)}
                placeholder="Enter reason for rejection"
                rows={3}
              />
            </div>
          )}

          <div className="flex flex-col mt-3">
            <label>2FA Code</label>
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              autoFocus
              className="w-full border px-2 py-1 rounded"
              value={otp}
              onChange={(e) => {
                const onlyNums = e.target.value.replace(/\D/g, ""); // keep only digits
                setOtp(onlyNums);
              }}
            />
            {otpError && <div className="text-red-500 text-sm">{otpError}</div>}
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default ManualTransfer;
